import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { isDark, isWhite, lightenDarkenColor } from 'common-logic';
import { PromotionProductsData } from '@evermile/customer-tracking';

export interface PromotionDetails {
  promotionId: string;
  promotionCode: string;
  expiresAt?: Date;
  productsDiscount?: number;
}

@Component({
  selector: 'common-fab-promotion-products',
  templateUrl: './fab-promotion-products.component.html',
  styleUrls: ['./fab-promotion-products.component.scss'],
})
export class FabPromotionProductsComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) brandColor!: string;
  @Input({ required: true }) elementsBackgroundColor!: string;
  @Input({ required: true }) textColor!: string;
  @Input() allowOrderEdit?: boolean;
  @Input() promotionProductsScrollState = {
    shouldCollapse: false,
    shouldHide: false,
  };
  @Input({ required: true }) promotionDetails!: PromotionDetails;
  @Input({ required: true }) promotionProductsData!: PromotionProductsData;
  @Input({ required: true }) orderStatus?: string;
  @Output() scrollToProductsCard = new EventEmitter();
  @Output() promotionFabDisplayChange = new EventEmitter<
    'invisible' | 'minimized-large' | 'minimized-small' | 'open'
  >();

  enableOrderEdit = false;
  bannerTitle = '';
  bannerDescription = '';
  expired = true;

  gradiantColor = '';
  gradiantSecondaryColor = '';
  borderColor = '';
  borderShadowColor = '';
  timerTextColor = '';
  buttonColor = '';

  extended = true;

  countdown = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  };

  ngOnInit(): void {
    if (this.promotionDetails.expiresAt) {
      const expirationTime = this.promotionDetails.expiresAt.getTime();
      const timeLeft = expirationTime - new Date().getTime();

      if (timeLeft > 0) {
        this.enableOrderEdit = !!this.allowOrderEdit && false;
        this.extended = false;
        this.setCountdown(expirationTime - new Date().getTime());

        setInterval(() => {
          this.setCountdown(expirationTime - new Date().getTime());
        }, 1000);
      }
    }
    this.promotionFabDisplayChange.emit(this.enableOrderEdit ? 'minimized-large' : 'minimized-small');
    if (this.enableOrderEdit) {
      this.bannerTitle = 'Forgot to buy something?';
      if (this.promotionDetails?.productsDiscount) {
        this.bannerDescription = `Add to this order with ${this.promotionDetails.productsDiscount}% OFF`;
      } else {
        this.bannerDescription = 'You can still add items to this order';
      }
    } else {
      if (this.promotionDetails?.productsDiscount) {
        this.bannerTitle = `${this.promotionDetails.productsDiscount}% OFF specific products just for you`;
      } else {
        this.bannerTitle = 'See more products you may like';
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['brandColor']) {
      if (isWhite(this.brandColor)) {
        this.timerTextColor = '#FFFFFF';
        this.gradiantColor = '#343E57';
        this.gradiantSecondaryColor = lightenDarkenColor('#343E57', -20);
        this.borderColor = '#FFFFFF';
        this.borderShadowColor = '#FFFFFF';
        this.buttonColor = '#FFFFFF';
        this.textColor = '#FFFFFF';
      } else if (isDark(this.brandColor)) {
        this.timerTextColor = lightenDarkenColor(this.brandColor, 100);
        this.gradiantColor = this.brandColor;
        this.gradiantSecondaryColor = lightenDarkenColor(this.brandColor, 20);
        this.borderColor = '#FFFFFF';
        this.borderShadowColor = lightenDarkenColor(this.brandColor, -60);
        this.buttonColor = '#FFFFFF';
      } else {
        this.timerTextColor = lightenDarkenColor(this.brandColor, -90);
        this.gradiantColor = this.brandColor;
        this.gradiantSecondaryColor = lightenDarkenColor(this.brandColor, -20);
        this.borderColor = lightenDarkenColor(this.brandColor, -50);
        this.borderShadowColor = lightenDarkenColor(this.brandColor, -50);
        this.buttonColor = '#343E57';
      }
    }
    if (changes['promotionProductsData']) {
      this.promotionProductsData = { ...this.promotionProductsData, galleryType: 'carousel' };
    }
    if (!this.enableOrderEdit && changes['promotionProductsScrollState']) {
      this.extended = !this.promotionProductsScrollState.shouldCollapse;
    }
  }

  private setCountdown(timeLeft: number) {
    if (!this.promotionDetails?.expiresAt) {
      return;
    }

    this.countdown.days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    this.countdown.hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    this.countdown.minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    this.countdown.seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
  }

  toggleExtended() {
    this.extended = !this.extended;
    this.promotionFabDisplayChange.emit(
      this.extended ? 'open' : this.enableOrderEdit ? 'minimized-large' : 'minimized-small',
    );
  }

  ngOnDestroy() {
    this.promotionFabDisplayChange?.emit('invisible');
  }
}
